import React, { useState, useRef, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Search, LayoutDashboard, Calendar, FileText, Sparkles, Image, BarChart3,
  Users, Settings, Radio, Workflow, FlaskConical, Palette, Webhook,
  LayoutTemplate, FileBarChart, ChevronDown, ChevronRight, ExternalLink,
  BookOpen, ArrowUp, Monitor, MousePointerClick, Zap, Hash, Clock,
  Target, MessageSquare, Eye, TrendingUp, Upload, Filter, Star,
  Shield, Bell, CreditCard, Globe, Play
} from 'lucide-react';
import { Link } from 'react-router-dom';

const GUIDE_SECTIONS = [
  {
    id: 'getting-started',
    icon: Zap,
    title: 'Getting Started',
    subtitle: 'Your first steps with StelliSocial',
    color: 'emerald',
    screenshot: null,
    items: [
      {
        title: 'Welcome to StelliSocial',
        steps: [
          'Log in with your email and password at the Sign In page.',
          'You\'ll land on the Dashboard -- your command center showing stats, upcoming posts, and recent activity.',
          'Use the sidebar on the left to navigate between all features.',
          'Start by creating a Calendar (for a brand or campaign), then compose your first post.',
          'Invite team members to collaborate, set up approval workflows, and connect social accounts.',
        ],
      },
      {
        title: 'Connecting Social Accounts',
        steps: [
          'Open any Calendar, then navigate to its Settings page.',
          'Under "Connected Accounts", click the Connect button next to a social platform.',
          'Authorize StelliSocial in the popup window that appears.',
          'Supported platforms: Facebook, Instagram, Twitter/X, LinkedIn, YouTube, TikTok, Pinterest, Threads, Bluesky, Snapchat, Google Business Profile.',
        ],
      },
    ],
  },
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    title: 'Dashboard',
    subtitle: 'Your content command center at a glance',
    color: 'slate',
    link: '/dashboard',
    screenshot: '/guide/dashboard.jpg',
    items: [
      {
        title: 'Understanding the Dashboard',
        steps: [
          'Performance Cards at the top show Total Posts, Scheduled, Published, and Engagement metrics with percentage trends.',
          'This Week section displays a weekly calendar strip with scheduled posts for each day.',
          'Calendars panel on the right lists all your calendars with their post counts.',
          'Upcoming Posts shows posts scheduled for the next 7 days with quick access.',
          'Needs Attention highlights pending approvals and draft posts requiring action.',
          'Performance Overview at the bottom shows Total Reach, Impressions, Engagement, and Engagement Rate.',
        ],
      },
      {
        title: 'Quick Actions',
        steps: [
          'Click "Get Ideas" in the top-right to jump to AI-powered content brainstorming.',
          'Click "+ Create Post" to open the Post Composer directly from the dashboard.',
          'Click "View Calendar" to see your full monthly content calendar.',
          'Click on any post in "Needs Attention" to quickly review or edit it.',
        ],
      },
    ],
  },
  {
    id: 'calendars',
    icon: Calendar,
    title: 'Calendars',
    subtitle: 'Organize content by brand, client, or campaign',
    color: 'blue',
    link: '/calendars',
    screenshot: '/guide/calendars.jpg',
    items: [
      {
        title: 'Managing Calendars',
        steps: [
          'Click "+ New Calendar" (top-right) to create a calendar for a brand, client, or campaign.',
          'Give it a name, description, and optionally a color tag for easy identification.',
          'Each calendar has its own connected social accounts and independent set of posts.',
          'Calendar cards show the number of posts and connected accounts at a glance.',
          'Click "Open Calendar" on any card to view its monthly grid of scheduled content.',
        ],
      },
      {
        title: 'Calendar View & Scheduling',
        steps: [
          'In the calendar view, each day cell shows scheduled posts as colored dots or previews.',
          'Click on any date to create a new post for that specific day.',
          'Drag and drop posts between dates to quickly reschedule content.',
          'Use the month navigation arrows to browse past and future content plans.',
        ],
      },
      {
        title: 'AI Calendar Auto-fill',
        steps: [
          'Open any calendar and look for the "AI Auto-fill" button (sparkle icon).',
          'Set the content theme, tone, target platforms, and desired posts per day.',
          'Click "Generate Week" -- AI creates a complete content plan for the upcoming week.',
          'Review all generated posts in the preview panel, then click "Apply to Calendar".',
          'Posts are added as editable drafts so you can refine them before scheduling.',
        ],
      },
    ],
  },
  {
    id: 'posts',
    icon: FileText,
    title: 'Posts',
    subtitle: 'View, filter, and manage all your content',
    color: 'indigo',
    link: '/posts',
    screenshot: '/guide/posts.jpg',
    items: [
      {
        title: 'Browsing Posts',
        steps: [
          'The Posts page lists all posts across all calendars with their current status.',
          'Each post shows its status badge (Draft, Published, Scheduled, etc.), calendar name, content preview, and platform icons.',
          'Use the search bar to find posts by text content.',
          'Filter by Status (All Statuses, Draft, Scheduled, Published, etc.) using the dropdown.',
          'Filter by Calendar or Platform to narrow down results.',
        ],
      },
      {
        title: 'Post Statuses Explained',
        steps: [
          'Draft -- Saved but not yet submitted for review. You can still edit freely.',
          'Pending Review -- Submitted and waiting for team approval.',
          'Approved -- Reviewed by a team member and ready to be scheduled.',
          'Scheduled -- Queued for automatic publishing at the set date and time.',
          'Published -- Successfully posted to your connected social platforms (shows "Live" badge).',
          'Failed -- Something went wrong. Check the error details and retry.',
        ],
      },
      {
        title: 'Post Actions',
        steps: [
          'Click "Submit" on draft posts to send them for team approval.',
          'Click the three-dot menu on any post for options like Edit, Delete, Duplicate.',
          'Click "+ Create Post" (top-right) to compose a brand new post.',
        ],
      },
    ],
  },
  {
    id: 'post-composer',
    icon: MousePointerClick,
    title: 'Post Composer',
    subtitle: 'Create, write, and schedule content with AI assistance',
    color: 'violet',
    link: '/posts/new',
    screenshot: '/guide/post-composer.jpg',
    items: [
      {
        title: 'Composing a Post',
        steps: [
          'Select a Calendar from the dropdown at the top to associate this post.',
          'Choose target Platforms by clicking their cards (Facebook, Instagram, Twitter/X, LinkedIn, YouTube, TikTok, Pinterest, Threads, Bluesky).',
          'Write your content in the text area under "Content". A character counter tracks limits per platform.',
          'Add Hashtags below the content area -- type and click "Add" or press Enter.',
          'Toggle "Schedule" on the right sidebar to set a future publish date and time.',
          'Use "Performance Prediction" to get an AI score before publishing.',
          'Click "Save Draft" to save without publishing, or "Publish Now" to post immediately.',
        ],
      },
      {
        title: 'AI Writing Tools',
        steps: [
          '"AI Write" -- Generate captions by entering a topic, desired tone, and length.',
          '"AI Hashtags" -- Automatically generate relevant hashtags based on your content.',
          '"Video Studio" -- Generate short AI videos in Landscape, Square, or Portrait formats.',
          '"Image Studio" -- Create AI-generated images for your posts.',
          'Each AI tool has an "Apply" button to insert the generated content directly into your post.',
        ],
      },
      {
        title: 'Performance Prediction',
        steps: [
          'In the right sidebar, find "Performance Prediction" and click "Predict".',
          'AI analyzes your content, platforms, and hashtags to give a score from 0-100.',
          'Ratings are: Excellent (80+), Good (60-79), Average (40-59), or Needs Work (below 40).',
          'Predicted metrics include: Reach, Engagement, Clicks, and Engagement Rate.',
          'Click "View Score Factors" to see what\'s driving your score (Content Quality, Media, Timing, etc.).',
          'AI provides specific improvement suggestions with High/Medium/Low impact badges.',
        ],
      },
    ],
  },
  {
    id: 'content-ideas',
    icon: Sparkles,
    title: 'Content Ideas',
    subtitle: 'AI-powered inspiration for your social media',
    color: 'amber',
    link: '/ideas',
    screenshot: '/guide/content-ideas.jpg',
    items: [
      {
        title: 'Generating Ideas',
        steps: [
          'Navigate to "Content Ideas" in the sidebar.',
          'Enter a Topic (e.g., "Product launch tips") in the input field, or leave blank for general ideas.',
          'Optionally select an Industry from the dropdown (Technology, Healthcare, Finance, etc.).',
          'Filter by target Platforms (Facebook, Instagram, Twitter/X, LinkedIn, TikTok).',
          'Click "Generate 5 Ideas" -- AI creates titled ideas with full descriptions and relevant hashtags.',
        ],
      },
      {
        title: 'Working with Ideas',
        steps: [
          'Each generated idea shows a title, detailed description, and suggested hashtags.',
          'Click the bookmark icon to save ideas you like to the "Saved" tab.',
          'Switch between "All Ideas" and "Saved" tabs at the top to manage your collection.',
          'Click "Convert to Post" at the bottom of any idea to turn it into a draft post.',
          'Select a calendar during conversion, and the idea becomes a ready-to-edit post.',
        ],
      },
    ],
  },
  {
    id: 'templates',
    icon: LayoutTemplate,
    title: 'Templates',
    subtitle: 'Pre-built content templates to speed up creation',
    color: 'teal',
    link: '/templates',
    screenshot: '/guide/templates.jpg',
    items: [
      {
        title: 'Browsing Templates',
        steps: [
          'Go to "Templates" in the sidebar to see the full template library.',
          'Browse by category using the filter chips: Promotional, Educational, Engagement, Announcement, Seasonal, Product, Tips, Event.',
          'Use the search bar to find templates by name or content.',
          'Each template card shows its category badge, content preview, and supported platforms.',
        ],
      },
      {
        title: 'Using Templates',
        steps: [
          'Click "Use" on any template to open it in the Post Composer with content pre-filled.',
          'Fill in template variables (shown as {{variable_name}}) with your specific details.',
          'Click "+ Create Template" (top-right) to build your own reusable templates.',
          'Custom templates appear alongside the built-in library for your whole team.',
        ],
      },
    ],
  },
  {
    id: 'media',
    icon: Image,
    title: 'Media Library',
    subtitle: 'Upload, organize, and manage all your visual assets',
    color: 'sky',
    link: '/media',
    screenshot: '/guide/media-library.jpg',
    items: [
      {
        title: 'Managing Media',
        steps: [
          'Navigate to "Media Library" in the sidebar to see all uploaded assets.',
          'Click "Upload Media" (top-right) or drag-and-drop files directly onto the page.',
          'Supported formats: Images (JPG, PNG, GIF, WebP) and Videos (MP4, MOV).',
          'Media displays in a grid view with thumbnails, filenames, and file sizes.',
          'Toggle between Grid and List views using the icons in the top-right.',
          'Filter by Folder or Calendar to organize assets by project.',
          'Use the search bar to quickly find files by name.',
          'Hover over any media item to see download and delete action buttons.',
        ],
      },
    ],
  },
  {
    id: 'analytics',
    icon: BarChart3,
    title: 'Analytics',
    subtitle: 'Track performance across all your social platforms',
    color: 'amber',
    link: '/analytics',
    screenshot: '/guide/analytics.jpg',
    items: [
      {
        title: 'Analytics Dashboard',
        steps: [
          'Top metric cards show Total Reach, Impressions, Engagement, and Clicks with trend percentages.',
          'Filter by Calendar and Date Range (Last 7/30/90 days) using the dropdowns at top-right.',
          'Click "Export" to download your analytics data.',
          'Click "Refresh" to pull the latest numbers.',
        ],
      },
      {
        title: 'Analytics Tabs',
        steps: [
          'Overview -- Performance Trend chart (Reach vs. Engagement over time) and Reach by Platform pie chart.',
          'Posts -- Individual post metrics with sorting and filtering by performance.',
          'Engagement -- Detailed breakdown of likes, comments, shares, and saves.',
          'Audience -- Demographics and geographic distribution of your followers.',
          'Best Times -- Heatmap showing optimal posting times for maximum engagement.',
        ],
      },
    ],
  },
  {
    id: 'social-listening',
    icon: Radio,
    title: 'Social Listening',
    subtitle: 'Monitor brand mentions, keywords, and sentiment',
    color: 'cyan',
    link: '/listening',
    screenshot: '/guide/social-listening.jpg',
    items: [
      {
        title: 'Setting Up Topics',
        steps: [
          'Go to "Social Listening" in the sidebar.',
          'Click "+ New Topic" (top-right) to create a monitoring topic.',
          'Enter a Topic Name (e.g., "Brand Mentions"), add Keywords (comma-separated), and select Platforms.',
          'Click "Create Topic" -- StelliSocial automatically starts tracking mentions.',
        ],
      },
      {
        title: 'Reading the Dashboard',
        steps: [
          'The Dashboard tab shows 4 key metrics: Active Topics, Total Mentions, This Week, and Top Keywords.',
          'Sentiment Breakdown displays positive, neutral, and negative percentages as horizontal bars.',
          'Mention Volume (7 days) shows a daily bar chart of mention counts.',
          'Top Keywords highlights the most frequently appearing terms in your mentions.',
          'Platform Distribution shows which social networks generate the most mentions.',
        ],
      },
      {
        title: 'Mentions & AI Analysis',
        steps: [
          'Switch to the "Mentions" tab to see individual mention cards with author, content, and engagement.',
          'Filter mentions by Topic, Sentiment, or Platform using the dropdowns.',
          'Switch to the "Topics" tab to manage and edit your monitoring topics.',
          'Click "Generate Analysis" at the bottom for an AI-powered brand sentiment report with actionable insights.',
        ],
      },
    ],
  },
  {
    id: 'workflows',
    icon: Workflow,
    title: 'Workflows',
    subtitle: 'Automate repetitive tasks with trigger-action rules',
    color: 'orange',
    link: '/workflows',
    screenshot: '/guide/workflows.jpg',
    items: [
      {
        title: 'Creating Workflows',
        steps: [
          'Go to "Workflows" in the sidebar and click "+ New Workflow" (top-right).',
          'Choose "From Template" for pre-built automations (auto-approve posts with images, add brand hashtags, notify team on publish, etc.).',
          'Or choose "Custom" to build your own: set a Trigger (Post Created, Scheduled, Published, Keyword Match, Engagement Threshold), then add Actions (Auto-approve, Add Hashtags, Notify Team, Change Status, Add Label).',
          'Click "Create Workflow" to save and activate.',
        ],
      },
      {
        title: 'Managing Workflows',
        steps: [
          'Each workflow card shows the trigger-to-action flow visually (e.g., "Post Created" -> "Auto-approve").',
          'Use the toggle switch to activate or pause a workflow instantly.',
          'Click "Test" to simulate the workflow without affecting real data.',
          'Switch to "Execution Logs" tab to see past triggers, actions, and timestamps.',
          'Stats at the top show: Total workflows, Active, Executions, and Paused counts.',
        ],
      },
    ],
  },
  {
    id: 'ab-testing',
    icon: FlaskConical,
    title: 'A/B Testing',
    subtitle: 'Compare post variants to optimize engagement',
    color: 'pink',
    link: '/ab-testing',
    screenshot: '/guide/ab-testing.jpg',
    items: [
      {
        title: 'Creating an A/B Test',
        steps: [
          'Go to "A/B Testing" in the sidebar and click "+ New A/B Test" (top-right).',
          'Enter a Test Name (e.g., "CTA Style Test").',
          'Choose a Success Metric: Engagement Rate, Clicks, Reach, or Total Engagement.',
          'Select target Platforms (Facebook, Instagram, etc.).',
          'Write content for each Variant (minimum 2, maximum 5).',
          'Tip: Write Variant A first, then use "AI Generate" to auto-create alternatives.',
          'Click "Create & Run Test" to start.',
        ],
      },
      {
        title: 'Reading Results',
        steps: [
          'The overview shows Total Tests, Completed, Running, and Average Improvement stats.',
          'Click on any test to expand and see variant comparison bars with relative performance.',
          'The winning variant is highlighted with a "Winner" badge.',
          'Each variant displays: Reach, Likes, Comments, Shares, and Clicks.',
          'Click "AI Analysis" for a detailed explanation of why the winner performed better, with actionable lessons.',
        ],
      },
    ],
  },
  {
    id: 'brand-guidelines',
    icon: Palette,
    title: 'Brand Guidelines',
    subtitle: 'Define and enforce brand consistency across all content',
    color: 'rose',
    link: '/brand-guidelines',
    screenshot: '/guide/brand-guidelines.jpg',
    items: [
      {
        title: 'Setting Up Guidelines',
        steps: [
          'Go to "Brand Guidelines" in the sidebar.',
          'Click "Start from Preset" to choose a pre-built guideline set (Corporate, Lifestyle, Tech, E-commerce).',
          'Or click "+ Create Custom" to build your own from scratch.',
          'Set your Color Palette, Typography, and Voice & Tone preferences.',
          'Define Content Rules: caption length limits, hashtag limits, and forbidden words.',
        ],
      },
      {
        title: 'Using Guidelines',
        steps: [
          'Switch between "My Guidelines" and "Presets" tabs to manage your collection.',
          'Click "Validate Content" (top-right) to check any text against your brand rules.',
          'When guidelines are enabled, the Post Composer validates content in real time as you type.',
          'Violations appear as inline warnings so you can fix them before publishing.',
        ],
      },
    ],
  },
  {
    id: 'webhooks',
    icon: Webhook,
    title: 'Webhooks',
    subtitle: 'Connect custom publishing channels and event triggers',
    color: 'slate',
    link: '/webhooks',
    screenshot: '/guide/webhooks.jpg',
    items: [
      {
        title: 'Custom Channels',
        steps: [
          'Go to "Webhooks" in the sidebar -- you\'ll see two tabs: "Custom Channels" and "Webhooks".',
          'Click "+ Add Channel" to create a new custom publishing target.',
          'Enter a Name, Webhook URL, and optional Description.',
          'Use the toggle to enable/disable the channel without deleting it.',
          'Click "Test" to send a sample payload and verify delivery to your endpoint.',
          'Custom channels appear in the Post Composer alongside standard social platforms.',
          'Great for: Company blogs, Slack, Discord, internal CMS systems, or any webhook-capable service.',
        ],
      },
      {
        title: 'Event Webhooks',
        steps: [
          'Switch to the "Webhooks" tab for event-driven notifications.',
          'Create webhooks that fire when specific events happen (post published, approved, etc.).',
          'Use these to integrate StelliSocial with your existing tools and workflows.',
        ],
      },
    ],
  },
  {
    id: 'team',
    icon: Users,
    title: 'Team Management',
    subtitle: 'Invite members, assign roles, and set permissions',
    color: 'purple',
    link: '/team',
    screenshot: '/guide/team.jpg',
    items: [
      {
        title: 'Managing Your Team',
        steps: [
          'Go to "Team" in the sidebar to see all members and their roles.',
          'Your account appears at the top with the "Owner" badge.',
          'Click "Invite Member" (top-right) to add a new team member by email.',
        ],
      },
      {
        title: 'Roles & Permissions',
        steps: [
          'Owner -- Full access to everything, including billing and team management.',
          'Manager -- Can manage content and team members (no billing access).',
          'Editor -- Can create and edit content, but cannot manage team.',
          'Client -- Can view content and approve posts, but cannot create.',
          'Viewer -- Read-only access to all content.',
          'The Role Permissions chart at the bottom of the page explains each role clearly.',
        ],
      },
    ],
  },
  {
    id: 'reports',
    icon: FileBarChart,
    title: 'Reports',
    subtitle: 'Build and export custom analytics reports',
    color: 'emerald',
    link: '/reports',
    screenshot: '/guide/reports.jpg',
    items: [
      {
        title: 'Creating Reports',
        steps: [
          'Go to "Reports" in the sidebar.',
          'Click "+ Create Report" (top-right) to start a new report.',
          'Choose from pre-built Templates or start with a blank report.',
          'Select metrics, date range, calendars, and platform filters.',
          'Click "Generate" to build the report with charts and data tables.',
          'Switch between "My Reports" and "Templates" tabs to manage your library.',
        ],
      },
      {
        title: 'Exporting & Sharing',
        steps: [
          'Export reports as PDF for client presentations.',
          'Export as CSV for further data analysis in spreadsheets.',
          'Schedule automated report delivery to stakeholders via email.',
        ],
      },
    ],
  },
  {
    id: 'settings',
    icon: Settings,
    title: 'Settings & Billing',
    subtitle: 'Manage your account, plan, and preferences',
    color: 'slate',
    link: '/settings',
    screenshot: '/guide/settings.jpg',
    items: [
      {
        title: 'Profile Settings',
        steps: [
          'Go to "Settings" at the bottom of the sidebar.',
          'Profile tab: Update your full name, email, and profile photo (JPG, PNG, or GIF, max 2MB).',
          'Click "Save Changes" to apply updates.',
        ],
      },
      {
        title: 'Other Settings',
        steps: [
          'Billing tab: View your current plan and usage. Plans: Base ($32/mo), Standard ($64/mo), Advanced ($131/mo). Click "Upgrade" for Stripe checkout.',
          'Notifications tab: Configure email alerts, push notifications, and digest email frequency.',
          'Security tab: Change your password and manage active sessions.',
        ],
      },
    ],
  },
];

const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-700', icon: 'text-emerald-500', border: 'border-emerald-400', accent: 'bg-emerald-500', light: 'bg-emerald-50' },
  blue: { bg: 'bg-blue-500/10', text: 'text-blue-700', icon: 'text-blue-500', border: 'border-blue-400', accent: 'bg-blue-500', light: 'bg-blue-50' },
  indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-700', icon: 'text-indigo-500', border: 'border-indigo-400', accent: 'bg-indigo-500', light: 'bg-indigo-50' },
  violet: { bg: 'bg-violet-500/10', text: 'text-violet-700', icon: 'text-violet-500', border: 'border-violet-400', accent: 'bg-violet-500', light: 'bg-violet-50' },
  purple: { bg: 'bg-purple-500/10', text: 'text-purple-700', icon: 'text-purple-500', border: 'border-purple-400', accent: 'bg-purple-500', light: 'bg-purple-50' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-700', icon: 'text-amber-500', border: 'border-amber-400', accent: 'bg-amber-500', light: 'bg-amber-50' },
  cyan: { bg: 'bg-cyan-500/10', text: 'text-cyan-700', icon: 'text-cyan-500', border: 'border-cyan-400', accent: 'bg-cyan-500', light: 'bg-cyan-50' },
  orange: { bg: 'bg-orange-500/10', text: 'text-orange-700', icon: 'text-orange-500', border: 'border-orange-400', accent: 'bg-orange-500', light: 'bg-orange-50' },
  pink: { bg: 'bg-pink-500/10', text: 'text-pink-700', icon: 'text-pink-500', border: 'border-pink-400', accent: 'bg-pink-500', light: 'bg-pink-50' },
  teal: { bg: 'bg-teal-500/10', text: 'text-teal-700', icon: 'text-teal-500', border: 'border-teal-400', accent: 'bg-teal-500', light: 'bg-teal-50' },
  sky: { bg: 'bg-sky-500/10', text: 'text-sky-700', icon: 'text-sky-500', border: 'border-sky-400', accent: 'bg-sky-500', light: 'bg-sky-50' },
  rose: { bg: 'bg-rose-500/10', text: 'text-rose-700', icon: 'text-rose-500', border: 'border-rose-400', accent: 'bg-rose-500', light: 'bg-rose-50' },
  slate: { bg: 'bg-slate-500/10', text: 'text-slate-700', icon: 'text-slate-500', border: 'border-slate-400', accent: 'bg-slate-500', light: 'bg-slate-50' },
};

function TableOfContents({ sections, activeId, onNavigate }) {
  return (
    <nav className="space-y-0.5" data-testid="guide-toc">
      {sections.map(section => {
        const Icon = section.icon;
        const colors = COLOR_MAP[section.color] || COLOR_MAP.slate;
        const isActive = activeId === section.id;
        return (
          <button
            key={section.id}
            onClick={() => onNavigate(section.id)}
            data-testid={`toc-${section.id}`}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-all duration-200 ${
              isActive
                ? `${colors.light} ${colors.text} font-semibold`
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? colors.icon : 'text-slate-400'}`} />
            <span className="truncate">{section.title}</span>
          </button>
        );
      })}
    </nav>
  );
}

function ScreenshotViewer({ src, alt }) {
  const [expanded, setExpanded] = useState(false);
  if (!src) return null;
  return (
    <>
      <div
        className="relative mt-4 mb-2 rounded-xl overflow-hidden border border-slate-200 shadow-sm cursor-pointer group"
        onClick={() => setExpanded(true)}
        data-testid={`screenshot-${alt}`}
      >
        <img
          src={src}
          alt={alt}
          className="w-full h-auto block"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 text-sm font-medium text-slate-700">
            <Eye className="h-4 w-4" /> Click to enlarge
          </div>
        </div>
      </div>
      {expanded && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setExpanded(false)}
          data-testid="screenshot-overlay"
        >
          <img
            src={src}
            alt={alt}
            className="max-w-full max-h-[90vh] rounded-xl shadow-2xl"
          />
          <button
            onClick={() => setExpanded(false)}
            className="absolute top-6 right-6 bg-white/20 hover:bg-white/40 text-white rounded-full p-2 transition-colors"
          >
            <ChevronDown className="h-5 w-5" />
          </button>
        </div>
      )}
    </>
  );
}

function GuideSection({ section, isExpanded, onToggle }) {
  const Icon = section.icon;
  const colors = COLOR_MAP[section.color] || COLOR_MAP.slate;
  const totalSteps = section.items.reduce((acc, item) => acc + item.steps.length, 0);

  return (
    <div
      id={`section-${section.id}`}
      className={`rounded-xl border transition-all duration-300 ${
        isExpanded ? `border-l-4 ${colors.border} shadow-sm` : 'border-slate-200 hover:border-slate-300'
      }`}
      data-testid={`guide-section-${section.id}`}
    >
      <button
        className="w-full flex items-center justify-between p-5 text-left transition-colors hover:bg-slate-50/60 rounded-t-xl"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          <div className={`w-11 h-11 rounded-xl ${colors.bg} flex items-center justify-center`}>
            <Icon className={`h-5 w-5 ${colors.icon}`} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-800">{section.title}</h2>
            <p className="text-xs text-slate-400 mt-0.5">{section.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-xs text-slate-400 border-slate-200 font-normal hidden sm:inline-flex">
            {section.items.length} topic{section.items.length > 1 ? 's' : ''} / {totalSteps} steps
          </Badge>
          {section.link && (
            <Link
              to={section.link}
              onClick={e => e.stopPropagation()}
              className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 bg-emerald-50 px-2.5 py-1 rounded-full transition-colors"
              data-testid={`open-${section.id}`}
            >
              Open <ExternalLink className="h-3 w-3" />
            </Link>
          )}
          {isExpanded
            ? <ChevronDown className="h-5 w-5 text-slate-400" />
            : <ChevronRight className="h-5 w-5 text-slate-400" />
          }
        </div>
      </button>

      {isExpanded && (
        <div className="px-5 pb-5 space-y-4 animate-in fade-in duration-200">
          {section.screenshot && (
            <ScreenshotViewer src={section.screenshot} alt={section.title} />
          )}
          {section.items.map((item, idx) => (
            <div key={idx} className="ml-2 sm:ml-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <div className={`w-5 h-5 rounded-md ${colors.bg} flex items-center justify-center text-xs font-bold ${colors.text}`}>
                  {idx + 1}
                </div>
                {item.title}
              </h3>
              <ol className="space-y-2 ml-7">
                {item.steps.map((step, si) => (
                  <li key={si} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                    <span className={`flex-shrink-0 w-5 h-5 rounded-full ${colors.bg} flex items-center justify-center text-[10px] font-semibold ${colors.text} mt-0.5`}>
                      {si + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FeatureGuidePage() {
  const [search, setSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState(new Set(['getting-started']));
  const [activeId, setActiveId] = useState('getting-started');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const contentRef = useRef(null);

  const toggleSection = (id) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpandedSections(new Set(GUIDE_SECTIONS.map(s => s.id)));
  const collapseAll = () => setExpandedSections(new Set());

  const navigateToSection = (id) => {
    setActiveId(id);
    setExpandedSections(prev => new Set([...prev, id]));
    const el = document.getElementById(`section-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const mainEl = document.querySelector('main');
    if (!mainEl) return;
    const handleScroll = () => {
      setShowScrollTop(mainEl.scrollTop > 400);
    };
    mainEl.addEventListener('scroll', handleScroll);
    return () => mainEl.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = entry.target.id.replace('section-', '');
            setActiveId(id);
          }
        }
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0 }
    );
    GUIDE_SECTIONS.forEach(s => {
      const el = document.getElementById(`section-${s.id}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const filtered = search.trim()
    ? GUIDE_SECTIONS.filter(s =>
        s.title.toLowerCase().includes(search.toLowerCase()) ||
        s.subtitle.toLowerCase().includes(search.toLowerCase()) ||
        s.items.some(i =>
          i.title.toLowerCase().includes(search.toLowerCase()) ||
          i.steps.some(st => st.toLowerCase().includes(search.toLowerCase()))
        )
      )
    : GUIDE_SECTIONS;

  return (
    <DashboardLayout>
      <div className="flex min-h-screen" data-testid="feature-guide-page">
        {/* Sticky Table of Contents - desktop only */}
        <aside className="hidden lg:block w-60 flex-shrink-0 border-r border-slate-100 bg-white sticky top-0 h-screen overflow-y-auto py-6 px-3">
          <div className="mb-4 px-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Contents</h3>
          </div>
          <TableOfContents sections={GUIDE_SECTIONS} activeId={activeId} onNavigate={navigateToSection} />
        </aside>

        {/* Main Content */}
        <div className="flex-1 max-w-[960px] mx-auto px-4 sm:px-6 py-8 space-y-6" ref={contentRef}>
          {/* Header */}
          <div className="space-y-1">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-emerald-500" />
                  </div>
                  User Guide
                </h1>
                <p className="text-sm text-slate-500 mt-2 ml-[52px]">
                  Complete how-to walkthroughs with screenshots for every StelliSocial feature.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={expandAll}
                  className="text-xs font-medium text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid="expand-all-btn"
                >
                  Expand All
                </button>
                <button
                  onClick={collapseAll}
                  className="text-xs font-medium text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid="collapse-all-btn"
                >
                  Collapse All
                </button>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search features, steps, or keywords..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 h-11"
              data-testid="guide-search"
            />
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-emerald-50 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-emerald-700">{GUIDE_SECTIONS.length}</div>
              <div className="text-xs text-emerald-600 mt-0.5">Feature Sections</div>
            </div>
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-blue-700">{GUIDE_SECTIONS.reduce((a, s) => a + s.items.length, 0)}</div>
              <div className="text-xs text-blue-600 mt-0.5">Topics Covered</div>
            </div>
            <div className="bg-violet-50 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-violet-700">{GUIDE_SECTIONS.reduce((a, s) => a + s.items.reduce((b, i) => b + i.steps.length, 0), 0)}</div>
              <div className="text-xs text-violet-600 mt-0.5">Step-by-step Instructions</div>
            </div>
            <div className="bg-amber-50 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-amber-700">{GUIDE_SECTIONS.filter(s => s.screenshot).length}</div>
              <div className="text-xs text-amber-600 mt-0.5">Screenshots</div>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-3" data-testid="guide-sections">
            {filtered.map(section => (
              <GuideSection
                key={section.id}
                section={section}
                isExpanded={expandedSections.has(section.id)}
                onToggle={() => toggleSection(section.id)}
              />
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <Search className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium">No matching features found</p>
              <p className="text-sm mt-1">Try different keywords for "{search}"</p>
            </div>
          )}

          {/* Footer */}
          <div className="text-center py-8 border-t border-slate-100 mt-8">
            <p className="text-sm text-slate-400">
              Need more help? Reach out to our support team or check the
              <Link to="/help" className="text-emerald-600 hover:text-emerald-700 ml-1">Help Center</Link>.
            </p>
          </div>
        </div>
      </div>

      {/* Scroll to top */}
      {showScrollTop && (
        <button
          onClick={() => {
            const mainEl = document.querySelector('main');
            if (mainEl) mainEl.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full bg-slate-800 text-white shadow-lg flex items-center justify-center hover:bg-slate-700 transition-colors"
          data-testid="scroll-top-btn"
        >
          <ArrowUp className="h-4 w-4" />
        </button>
      )}
    </DashboardLayout>
  );
}
